import type { JSX, ReactNode } from 'react';
import { cn } from '@lib/cn';

interface CardProps {
  /** Icone renderizado no topo do card. Caller passa pre-dimensionado (e.g. `<Wrench className="h-6 w-6" />`). D-04 convention. */
  readonly icon: ReactNode;
  /** Titulo do card. Renderizado como `<h3>` para preservar a outline semantica do documento (h2 da secao -> h3 dos cards). */
  readonly title: string;
  /** Descricao curta abaixo do titulo (2-3 linhas tipicas). */
  readonly description: string;
  readonly className?: string;
}

/**
 * Primitive `Card` reutilizavel — UI-02.
 *
 * Visual contract:
 *  - Surface: `rounded-2xl bg-white border border-neutral-200 shadow-sm`
 *  - Hover: `shadow-md` + `-translate-y-0.5` (eleva sutilmente — sensacao de
 *    "vivo" sem custo de animacao)
 *  - Padding: `p-6 sm:p-7`
 *  - Icon slot: circulo `bg-brand-100` de 48px com icone brand-700 dentro
 *  - Title: `font-display font-bold text-xl text-neutral-800` (h3)
 *  - Description: `text-base text-neutral-600 leading-relaxed mt-2`
 *
 * Usado em:
 *  - Servicos (SECT-04 Phase 4) — 4 cards lado a lado
 *  - Possivel reuso futuro: extras de Diferenciais ou KPIs da Phase 6
 *
 * NAO usado em:
 *  - Diferenciais (Phase 4) — visual de banda dark-green, layout diferente
 *  - FAQ (Phase 5) — UI-03 traz proprio primitive de accordion
 *
 * D-04 convention: o caller passa o icone pre-dimensionado (h-6 w-6 para
 * 24x24). O Card NAO auto-redimensiona — mantem responsabilidade no consumer
 * (mesmo padrao do Button).
 */
export const Card = ({ icon, title, description, className }: CardProps): JSX.Element => (
  <article
    className={cn(
      'group flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md sm:p-7',
      className,
    )}
  >
    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-brand-700 transition-colors group-hover:bg-brand-500 group-hover:text-white">
      {icon}
    </div>
    <h3 className="mt-5 font-display text-xl font-bold text-neutral-800">{title}</h3>
    <p className="mt-2 text-base leading-relaxed text-neutral-600">{description}</p>
  </article>
);
